import React, { useEffect, useState } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import ArticleIcon from '@mui/icons-material/Article';
import CloudQueueIcon from '@mui/icons-material/CloudQueue';
import { useNavigate } from 'react-router-dom';
import FlagIcon from '@mui/icons-material/Flag';
import SettingsIcon from '@mui/icons-material/Settings';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import FooterImg from "../../assets/images/Footer.png";
import NumbersIcon from '@mui/icons-material/Numbers';
import DeveloperBoardIcon from '@mui/icons-material/DeveloperBoard';
import { getPermissions } from "../../api/EnvironmentApi/environmentApi";
import {
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Typography,
  Divider,
  Box,
  CssBaseline,
  CircularProgress,
  Tooltip,
  TextField,
} from '@mui/material';
import {
  ExpandLess,
  ExpandMore,
  Menu as MenuIcon,
} from '@mui/icons-material';
import { postCall } from '../../api/httpService';
import { getFoldersData, getMenuData } from '../../api/menuApi/menuApi';
import VirtualizedList from './VirtualizedList';
import LocalStorageUtil from '../../utils/localStorage';
import { useAuth0 } from "@auth0/auth0-react";
const drawerWidth = 245;
const collapsedWidth = 70;
const hiddenMenuNames = ['Work Area 1','Requester - Email Template','Requester - File Locations','Requester - Account','System Statistics','System']; // case-sensitive
const customMenuOrder = [15, 1, 2, 3, 4];
type DrawerId = number | string | null;
export interface DrawerItem {
  id: number | string;
  parentId:DrawerId;
  parentFolderId?: DrawerId;
  name: string;
  icon?: string;
  type: 'menu' | 'folder' | 'account';
  routePath?: string | null;
  children?: DrawerItem[];
  isExpanded?: boolean;
  isBold?: boolean;
}
type DrawerComponentProps = {
  isCollapsed: boolean;
  onToggleCollapse: () => void;
};


function getPermittedData(menuData: any, allowedIds: any) {
  let permittedData = menuData.filter((item: any) => allowedIds.includes(item.menuId));
  let menuMap = new Map(menuData.map((item: any) => [item.menuId, item]));
  let index = 0;
  while (index < permittedData.length) {
      let currentItem = permittedData[index];
      let parentId = currentItem.parentMenuId;

      if (parentId !== null && !permittedData.some((item: any) => item.menuId === parentId)) {
          let parentMenu = menuMap.get(parentId);
          if (parentMenu) {
              permittedData.push(parentMenu);
          }
      }
      index++;
  }
  permittedData.sort((a: any, b: any) => a.menuId - b.menuId);
  return permittedData;
} 
const getMenus = async (): Promise<any[]> => {
  try {
    const payload = {
      UserId: LocalStorageUtil.getItem("userId"),
      accountId: LocalStorageUtil.getItem("accountId"),
      contextName: LocalStorageUtil.getItem("contextName"),
      isSuperAdmin: LocalStorageUtil.getItem("IsSuperAdmin"),
    };

    if (payload.UserId && payload.accountId && payload.contextName) {
      const response = await getMenuData(payload);
      const permisisonPayload = {
        UserId: LocalStorageUtil.getItem("userId"),
        accountId: LocalStorageUtil.getItem("accountId"),
        contextName: LocalStorageUtil.getItem("contextName"),
        isSuperAdmin: LocalStorageUtil.getItem("isSuperAdmin"),
        primaryGroupId: LocalStorageUtil.getItem("groupId"),
      };

      const permissionResponse = await getPermissions(permisisonPayload);
      const allowedMenuIds = permissionResponse.map((perm: any) => perm?.menuId);
      return getPermittedData(response, allowedMenuIds);
    } else {
      return []
    }
  } catch (error) {
    console.error("Error fetching menus:", error);
    return []; // Return an empty array to prevent breaking the UI
  }
};

const getFolders = async (parentId: number | string | null, depthPath: string, isUploadFiles: boolean): Promise<any[]> => {
  if (depthPath.startsWith('2.')) {
    depthPath = depthPath.replace('2.', '')
  }
  const payload = {
    accountId: LocalStorageUtil.getItem("accountId"),
    contextName: LocalStorageUtil.getItem("contextName"),
    depthPath: depthPath,
    folderId: parentId,
    folderRights: LocalStorageUtil.getItem("folderRights"),
    superAdmin: LocalStorageUtil.getItem("isSuperAdmin"),
    userId: LocalStorageUtil.getItem("userId"),
    isUploadFiles: isUploadFiles,
  };
  const response = await getFoldersData(payload)
  return response;
};

const getAccounts = async (): Promise<any[]> => {

  const payload = {
    userId: LocalStorageUtil.getItem("userId")
  };
  const response = await postCall("api/Account/GetAccountByUser", payload);
  if (response.status !== 200) {
    console.error('Error fetching folders:', response.statusText);
    return [];
  }
  return response.data;
};
const transformMenusToAccountItems = (accounts: any[]): DrawerItem[] =>
  accounts.map(folder => ({
    id: folder.accountName,
    parentId: folder.businessUnitId,
    parentFolderId: folder.contextName,
    name: folder.accountName,
    icon: folder.icon || '',
    type: 'account',
    routePath: '/newaccount',
    children: [],
  }));
const gettingMenuIcons = (menuName: string, icon: string): any => {
  switch (menuName) {
    case 'Search':
      return SearchIcon;
    case 'Accounts':
      return 'fa-solid fa-user-group';
    case 'Documents':
      return 'Article';
    case 'Queue Management':
      return 'fa-light fa-bars';
    case 'Work Area 2':
      return 'WorkArea2';
    default:
      return icon; // Default icon
  }
}
const transformMenusToDrawerItems = (menus: any[]): DrawerItem[] =>
  menus.filter(menu => !hiddenMenuNames.includes(menu.menuName)).map(menu => ({
    id: menu.menuId,
    parentId: menu.menuName === "Metadata Definition"?"1030":menu.parentMenuId, //Temporary fix for Metadata Definition, since it is a data issue of wrong parent id in DB
    name: menu.menuName,
    icon: gettingMenuIcons(menu.name, menu.icon) ?? menu.menuIcon,
    type: 'menu',
    routePath: menu.routePath,
    children: [],
    sortOrder: menu.orderBy,
  }));
const applyRootLevelSort = (tree: DrawerItem[]): DrawerItem[] => {
  const orderMap = new Map<number, number>();
  customMenuOrder.forEach((id, index) => orderMap.set(id, index));

  return [...tree].sort((a, b) => {
    const aIndex = orderMap.has(Number(a.id)) ? orderMap.get(Number(a.id))! : Number.MAX_SAFE_INTEGER;
    const bIndex = orderMap.has(Number(b.id)) ? orderMap.get(Number(b.id))! : Number.MAX_SAFE_INTEGER;

    return aIndex - bIndex;
  });
};

const transformFoldersToDrawerItems = (folders: any[], parentId: number | string | null): DrawerItem[] =>
  folders.map(folder => ({
    id: folder.fundFolderId,
    parentId: parentId,
    parentFolderId: parentId,
    name: folder.folderName,
    icon: 'foldericoncustom',
    type: 'folder',
    children: [],
  }));
const buildDepthPath = (item: DrawerItem, allItems: DrawerItem[]): string => {
  const path: (string | number)[] = [];

  let current: DrawerItem | undefined = item;
  while (current) {
    path.unshift(current.id);
    current = allItems.find(x => x.id === current?.parentFolderId);
  }

  return path.join('.');
};

const buildFolderPath = (item: DrawerItem, allItems: DrawerItem[]): string => {
  const path: (string | number)[] = [];

  let current: DrawerItem | undefined = item;
  while (current) {
    path.unshift(current.name);
    const abc = allItems.find(x => x.id === current?.parentFolderId);
    current = abc;
  }

  return path.join(' > ');
};
const buildTree = (items: DrawerItem[]): DrawerItem[] => {
  const lookup: Record<string, DrawerItem & { children: DrawerItem[] }> = {};
  const rootItems: DrawerItem[] = [];
  items.forEach(item => {
    lookup[item.id] = { ...item, children: item.children || [] };
  });

  Object.values(lookup).forEach(item => {
    if (item.parentId) {
      const parent = lookup[item.parentId];
      if (parent) {
        parent.children = parent.children || [];
        parent.children.push(item);
      }
    } else {
      rootItems.push(item);
    }
  });

  return rootItems;
};


const DrawerComponent: React.FC<DrawerComponentProps> = ({ isCollapsed, onToggleCollapse }) => {
  const { logout } = useAuth0();
  const [drawerItems, setDrawerItems] = useState<DrawerItem[]>([]);
  const [selectedPath, setSelectedPath] = useState<(number | string)[]>([]);
  const [loadingFolders, setLoadingFolders] = useState<(number | string)[]>([]);
  const navigate = useNavigate();
  const contextName = LocalStorageUtil.getItem("contextName");

  const [folderSearch, setFolderSearch] = useState<{
    [key: number | string]: string;
  }>({});
  const [isMenuLoading, setIsMenuLoading] = useState<boolean>(true);

  useEffect(() => {
    loadMenus();
  }, [contextName]);

  const loadMenus = async () => {
    try {
      setIsMenuLoading(true);
      let menus = await getMenus();
      const businessUnitId = Number(LocalStorageUtil.getItem("businessUnitId"));
      menus = menus.filter((menu) => {
        if (
          menu.menuId === 31 &&
          businessUnitId !== 35 &&
          businessUnitId !== 1025
        )
          return false;
        return true;
      });
      let menuItems = transformMenusToDrawerItems(menus);
      const documentsIndex = menuItems.findIndex((m) => m.name === "Documents");
      if (documentsIndex !== -1) {
        const folders = await getFolders(null, "", false);
        const folderItems = transformFoldersToDrawerItems(
          folders,
          menuItems[documentsIndex].id
        );
        menuItems[documentsIndex] = {
          ...menuItems[documentsIndex],
          children: folderItems,
        };
      }
      const accountsIndex = menuItems.findIndex((m) => m.name === "Accounts");
      if (accountsIndex !== -1) {
        const accounts = await getAccounts();
        const isSuperAdmin = Number(LocalStorageUtil.getItem("isSuperAdmin"));
        const businessUnitId = Number(
          LocalStorageUtil.getItem("businessUnitId")
        );
        let finalAccounts = [];
        if (isSuperAdmin === 1) {
          finalAccounts = accounts;
        } else {
          finalAccounts = accounts.filter(
            (account) => account.businessUnitId === businessUnitId
          );
        }
        const folderItems = transformMenusToAccountItems(finalAccounts);
        menuItems[accountsIndex] = {
          ...menuItems[accountsIndex],
          children: folderItems,
        };
      }

      const tree = buildTree(menuItems);
      const sortedTree = applyRootLevelSort(tree);
      setDrawerItems(sortedTree);
    } catch (error) {
      console.error("Error loading menus:", error);
    } finally {
      setIsMenuLoading(false);
    }
  };

  const collapseChildren = (item: DrawerItem) => {
    item.isExpanded = false;
    item.children?.forEach(child => collapseChildren(child));
  };
  const flattenDrawerItems = (items: DrawerItem[]): DrawerItem[] => {
    const result: DrawerItem[] = [];

    const flatten = (arr: DrawerItem[]) => {
      for (const item of arr) {
        result.push(item);
        if (item.children && item.children.length > 0) {
          flatten(item.children);
        }
      }
    };

    flatten(items);
    return result;
  };
  const handleClick = async (item: DrawerItem, path: (number | string)[]) => {
    const isLoggedIn = !!LocalStorageUtil.getItem("token"); // Change key as per your app

    if (!isLoggedIn) {
      // Redirect to login page
      LocalStorageUtil.removeItem("businessUnitId");
      LocalStorageUtil.removeItem("token");
      LocalStorageUtil.removeItem("antiforgery");
      LocalStorageUtil.removeItem("userName");
      logout({
        logoutParams: { returnTo: process.env.REACT_APP_CALLBACK_URL },
      });
      return;
    }
    try {
      if (item.children?.length === 0) {
        if (item.type === 'menu' && item.routePath) {
          navigate(item.routePath);
          return;
        }
        if (item.type === 'account' && item.routePath) {
          navigate(item.routePath, {
            state: { accountId: item.name, contextName: item.parentFolderId, businessUnitId: item.parentId },
          });
          return;
        }
      }
      let isUpload = false;
      if (item) {  
          const accountDetails = JSON.parse(
                  localStorage.getItem("authorizationData") ?? "{}"
                );
            const newAccountDetail = JSON.parse(accountDetails.customProcessValues);
            if (newAccountDetail && newAccountDetail.customProcessValuesObj.length > 0) {
              newAccountDetail.customProcessValuesObj.forEach((element: any) => {
                const newElement = element.folderId === undefined ? element.fundFolderId : element.folderId;
                let depthPath = 0; 
                if(depthPath !== undefined)
                  { 
                    depthPath = Number(path[1]);// NOSONAR
                  }
                if (newElement === depthPath) {
                  isUpload = true;
                }
              });
            }
      }
  
      if (item.type === 'folder') {
        const payload = {
          accountId: LocalStorageUtil.getItem("accountId"),
          contextName: LocalStorageUtil.getItem("contextName"),
          depthPath: path,
          folderId: item.id,
          folderRights: LocalStorageUtil.getItem("folderRights"),
          superAdmin: LocalStorageUtil.getItem("IsSuperAdmin"),
          userId: LocalStorageUtil.getItem("userId"),
          isUploadFiles: isUpload,
        };
        navigate(`/empty`, {
          state: { payload: payload, path: buildFolderPath(item, flattenDrawerItems(drawerItems)) },
          replace: true,
        });
      }
  
      if (!item.isExpanded && item.children?.length === 0 && item.type === 'folder') {
        setLoadingFolders((prev) => [...prev, item.id]); // Start loading
        const flatList = flattenDrawerItems(drawerItems);
        const depthPath = buildDepthPath(item, flatList);

        const subFolders = await getFolders(item.id, depthPath, isUpload);
        const subItems = transformFoldersToDrawerItems(subFolders, item.id);
        item.children = subItems;
  
        if (subItems.length === 0) {
          const payload = {
            accountId: LocalStorageUtil.getItem("accountId"),
            contextName: LocalStorageUtil.getItem("contextName"),
            depthPath: path,
            folderId: item.id,
            folderRights: LocalStorageUtil.getItem("folderRights"),
            superAdmin: LocalStorageUtil.getItem("IsSuperAdmin"),
            userId: LocalStorageUtil.getItem("userId"),
            isUploadFiles: isUpload,
          };
          navigate(`/documents`, {
            state: { payload: payload, path: buildFolderPath(item, flattenDrawerItems(drawerItems)) },
            replace: true,
          });
        }
        setLoadingFolders((prev) => prev.filter((id) => id !== item.id)); // Stop loading
      }
  
      if (item.isExpanded) {
        collapseChildren(item);
      } else {
        item.isExpanded = true;
      }
  
      setSelectedPath(path);
      setDrawerItems([...drawerItems]);
    } catch (error) {
      console.error("Error handling click:", error);
      // Optionally, show an error message to the user
    }
  };
  

  const isPathSelected = (itemId: number | string): boolean => selectedPath.includes(itemId);



  const renderItem = (item: DrawerItem, level = 0, path: (number | string)[] = []) => {
    const currentPath = [...path, item.id];
    const isSelected = isPathSelected(item.id);
    const isSuperParent = level === 0;

    return (
      <React.Fragment key={item.id}>
        <Tooltip title={item.name} placement="right" arrow>
          <ListItemButton
            onClick={() => handleClick(item, currentPath)}
            sx={{
              pl: 1 + level * 2, // Indentation
              py:  isSuperParent ? 1: 0.5,
              fontWeight: isSelected ? 'bold' : 'small',
              color: isSuperParent ? 'var(--aggrid-text-color)' : 'black', // default text color
              bgcolor: isSuperParent ? 'var(--primary-color)' : 'white', // default background
              '&:hover': {
                bgcolor: 'lightgray',
                color: 'black',
              },
              transition: 'background-color 0.2s ease-in-out',
            }}
          >
            {!isSuperParent ? item.icon === "foldericoncustom" ? <FolderOpenIcon style={{ marginRight: "5px" }} /> : <i className={"fa "+item.icon} style={{ marginRight: "5px" }}></i>
              : item.name === "Search" ? <SearchIcon style={{ marginRight: "5px" }} />
                : item.name === "Documents" ? <ArticleIcon style={{ marginRight: "5px" }} />
                  : item.name === "Queue Management" ? <CloudQueueIcon style={{ marginRight: "5px" }} />
                    : item.name === "Reports" ? <FlagIcon style={{ marginRight: "5px" }} />
                      : item.name === "Configuration" ? <SettingsIcon style={{ marginRight: "5px" }} />
                        : item.name === "System Statistics" ? <NumbersIcon style={{ marginRight: "5px" }} />
                          : item.name === "Activity" ? <DeveloperBoardIcon style={{ marginRight: "5px" }} />
                            : ""}
            {isCollapsed && (
              <ListItemText
                primary={
                  <Typography fontWeight={isSelected ? 'bold' : 'small'}>
                    {item.name}
                  </Typography>
                }
              />
            )}
            {item.children && item.children.length > 0 && (
              item.isExpanded ? <ExpandLess /> : <ExpandMore />
            )}
            {loadingFolders.includes(item.id) && (
              <CircularProgress size={18} sx={{ ml: 1 }} />
            )}
          </ListItemButton>
        </Tooltip>
        {item.children && (
          <Collapse in={item.isExpanded} timeout={0} unmountOnExit>
            <List component="div" disablePadding>
              {item.children.length > 100 ? (
                <>
                  <Box sx={{ p: 1, bgcolor: 'white', pl: 2 + level * 2,  }}>
                    <TextField
                    value={folderSearch?.[item.id] || ''}
                      fullWidth
                      variant="outlined"
                      size="small"
                      placeholder="Search..."
                      InputProps={{
                        startAdornment: (
                          <SearchIcon sx={{ mr: 0.5, color: 'gray', }} />
                        ),
                        sx: {
                          height: 28, // Search Box in Documents height
                          fontSize: '0.8rem',
                          paddingY: 0,
                        },
                      }}
                      onChange={(e) => {
                        setFolderSearch({...folderSearch, [item.id]:e.target.value});
                      }}
                      sx={{ fontSize: 'small' }}
                    />
                  </Box>                 
                  <VirtualizedList
                    key={`virtualized-list-${item.id}-${folderSearch?.[item.id] || ''}`} // Add key to force re-mount on filter change
                    items={item.children.filter(child => !folderSearch?.[item.id] || child.name.toLowerCase().includes(folderSearch?.[item.id].toLowerCase()))}
                    renderItem={(child) => renderItem(child, level + 1, currentPath)}
                    filterKey={folderSearch?.[item.id]}
                  />
                </>
              ) : (
                // Regular rendering for small lists
                item.children.map(child => renderItem(child, level + 1, currentPath))
              )}
            </List>
          </Collapse>
        )}
        <Divider sx={{ borderColor: 'var(--aggrid-text-color)' }} />
      </React.Fragment>
    );
  };

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <Drawer
        variant="permanent"
        open={!isCollapsed}
        sx={{
          width: isCollapsed ? drawerWidth : collapsedWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: isCollapsed ? drawerWidth : collapsedWidth,
            transition: "width 0.3s",
            overflowX: "hidden",
            display: "flex", 
            flexDirection: "column",
          },
        }}
        PaperProps={{
          style: {
            height: "92.5%",
            marginTop: "3.8em",
            maxWidth: "13%",
            backgroundColor: "var(--primary-color)",
          },
        }}
      >
        <Box sx={{ height: 8 }} />
        <Box sx={{ overflowY: "auto", flexGrow: 1 }}>
          <List>
            <ListItemButton
              // onClick={() => setDrawerOpen(prev => !prev)}
              onClick={onToggleCollapse}
              sx={{
                justifyContent: isCollapsed ? "flex-end" : "flex-start",
                bgcolor: "var(--primary-color)",
              }}
            >
              <ListItemIcon
                sx={{ minWidth: "auto", color: "var(--aggrid-text-color)" }}
              >
                <MenuIcon />
              </ListItemIcon>
            </ListItemButton>
            <Divider sx={{ borderColor: "black" }} />
            {isMenuLoading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "200px",
                  flexDirection: "column",
                }}
              >
                <CircularProgress
                  size={30}
                  sx={{ color: "var(--aggrid-text-color)" }}
                />
                <Typography
                  sx={{
                    mt: 2,
                    fontSize: "11px",
                    color: "var(--aggrid-text-color)",
                  }}
                >
                  Loading menus...
                </Typography>
              </Box>
            ) : (
              drawerItems.map((item) => renderItem(item))
            )}
          </List>
        </Box>
          {/* Footer stays at bottom with this approach */}
  {isCollapsed && (
    <Box
      sx={{
        backgroundColor: "lightgray",
        fontSize: "11px",
        height: "50px",
        textAlign: "center",
        color: "black",
        width: "100%", 
        paddingTop: "2px",
        marginTop: "auto", 
      }}
    >
      <span style={{ marginLeft: "10px" }}>
        Powered by <br />
        <img
          style={{
            width: "70%",            
            marginLeft: ".5%",
            display: "flex",
            margin: "0 auto",
            fontSize: "11px",
            height: "auto",
          }}
          src={FooterImg}
          alt="Powered by Doc-Distro"
        />
      </span>
    </Box>
  )}
    </Drawer>
    </Box>
  );
};

export default DrawerComponent;
